import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactFlow, {
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  ReactFlowProvider,
  SelectionMode,
  useNodesState,
  useReactFlow,
  Node,
  NodeChange,
  NodeProps,
  XYPosition,
} from 'reactflow';
import 'reactflow/dist/style.css';
import type {
  BoardNode,
  BoardNodeType,
  BoardImageData,
  BoardLinkData,
  BoardTimelineData,
  BoardFloorPlanData,
  BoardDrawing,
  BoardDrawTool,
} from '../../types';
import {
  subscribeToBoard,
  saveBoard,
  uploadBoardImage,
  deleteBoardImage,
  fetchLinkPreview,
  looksLikeUrl,
  normalizeUrl,
  domainFromUrl,
} from '../../utils/leadBoardService';
import { deleteBoardCommentsForNode } from '../../utils/boardCommentService';
import {
  publishBoardCursor,
  clearBoardCursor,
  cursorColorForUid,
} from '../../utils/boardPresenceService';
import BoardPresenceLayer from './BoardPresenceLayer';
import { useAuth } from '../../utils/AuthContext';
import { BoardActionsContext, BoardActions } from './boardActions';
import {
  DEFAULT_NODE_SIZE,
  DEFAULT_NOTE_COLOR,
  DEFAULT_BOARD_TEXT_STYLE,
  IMAGE_MAX_INITIAL_EDGE,
  IMAGE_CAPTION_HEIGHT,
  GROUP_PADDING,
  GROUP_LABEL_HEIGHT,
} from './boardConstants';
import ImageNode from './nodes/ImageNode';
import LinkNode from './nodes/LinkNode';
import NoteNode from './nodes/NoteNode';
import TextNode from './nodes/TextNode';
import GroupNode from './nodes/GroupNode';
import TimelineNode from './nodes/TimelineNode';
import FloorPlanNode from './nodes/FloorPlanNode';
import BoardToolbar, { BoardToolItem } from './BoardToolbar';
import BoardSnapshotPicker, {
  type BoardSnapshotModelChoice,
  type BoardSnapshotPdfChoice,
} from './BoardSnapshotPicker';
import HtmlViewerModal from '../html/HtmlViewerModal';
import { renderPdfPageToFile } from '../../utils/boardSnapshotCapture';
import BoardDrawToolbar from './BoardDrawToolbar';
import BoardDrawLayer, { BoardDrawBehind, type BoardDrawLayerHandle, type DrawingSelection } from './BoardDrawLayer';
import {
  drawingIsFront,
  drawingsSignature,
  imageCalloutsInFlow,
  imageMediaRect,
  initialsFromName,
  isFrameDrawing,
  maxNodeZIndex,
  nextImageRotation,
  nodeSize,
  rotateImageDrawingsCW,
  withImageCaption,
} from './drawUtils';
import { DRAW_WIDTHS, HIGHLIGHTER_WIDTHS } from './boardConstants';
import { rewriteGroupUnitDragChanges, type GroupUnitDrag } from './groupDrag';
import TimelineBuilderModal from './TimelineBuilderModal';
import FloorPlanEditor from './floorplan/FloorPlanEditor';
import {
  floorPlanEntityCount,
  liveDropsEntities,
  overlayLiveFloorPlans,
} from './floorplan/floorPlanMerge';
import { createDefaultFloorPlan } from './floorplan/floorPlanMath';
import { subscribeFloorPlans } from '../../utils/floorPlanLiveService';
import BoardSnapGuides from './BoardSnapGuides';
import {
  SNAP_SCREEN_PX,
  independentlyMoved,
  loadSnapEnabled,
  nodeToSnapRect,
  saveSnapEnabled,
  snapMovingRect,
  unionSnapRects,
  type SnapGuide,
} from './boardSnap';
import {
  BoardCommentsProvider,
  useBoardComments,
  type BoardCommentAuthor,
} from './comments/BoardCommentsContext';
import BoardCommentAnchor from './comments/BoardCommentAnchor';
import { GanttChart, Group, House, Ungroup } from 'lucide-react';
import './InspirationBoard.css';
import './comments/BoardComments.css';

const SAVE_DEBOUNCE_MS = 600;

// ---------------------------------------------------------------------------
// Conversion + signature helpers (board <-> reactflow)
// ---------------------------------------------------------------------------

function toRFNode(bn: BoardNode): Node {
  const hasSize = typeof bn.width === 'number' && typeof bn.height === 'number';
  return {
    id: bn.id,
    type: bn.type,
    position: { x: bn.x, y: bn.y },
    data: { ...bn.data },
    style: hasSize ? { width: bn.width, height: bn.height } : undefined,
    width: bn.width,
    height: bn.height,
    zIndex: typeof bn.zIndex === 'number' ? bn.zIndex : undefined,
    parentId: bn.parentId,
    extent: bn.parentId ? 'parent' : undefined,
  };
}

function toBoardNode(n: Node): BoardNode {
  const styleW = n.style?.width;
  const styleH = n.style?.height;
  const width = typeof styleW === 'number' ? styleW : n.width ?? undefined;
  const height = typeof styleH === 'number' ? styleH : n.height ?? undefined;
  return {
    id: n.id,
    type: n.type as BoardNodeType,
    x: Math.round(n.position.x),
    y: Math.round(n.position.y),
    width: width != null ? Math.round(Number(width)) : undefined,
    height: height != null ? Math.round(Number(height)) : undefined,
    zIndex: typeof n.zIndex === 'number' ? n.zIndex : undefined,
    parentId: n.parentId,
    data: n.data,
  };
}

function nodeDimensions(n: Node): { width: number; height: number } {
  const type = n.type as BoardNodeType;
  const defaults = DEFAULT_NODE_SIZE[type] || { width: 200, height: 200 };
  const w = Number(n.width ?? n.style?.width ?? defaults.width);
  const h = Number(n.height ?? n.style?.height ?? defaults.height);
  return { width: w, height: h };
}

function getAbsolutePosition(node: Node, nodes: Node[]): XYPosition {
  let x = node.position.x;
  let y = node.position.y;
  let parentId = node.parentId;
  while (parentId) {
    const parent = nodes.find((n) => n.id === parentId);
    if (!parent) break;
    x += parent.position.x;
    y += parent.position.y;
    parentId = parent.parentId;
  }
  return { x, y };
}

function stableData(d: Record<string, unknown>): string {
  // Ignore `undefined` values: Firestore strips them on write, so the echoed
  // snapshot omits those keys and the signature must match that stripped form.
  return Object.keys(d)
    .filter((k) => d[k] !== undefined)
    .sort()
    .map((k) => `${k}=${JSON.stringify(d[k])}`)
    .join(',');
}

/** Order-independent signature used to detect echoes of our own saves. */
function boardSignature(nodes: BoardNode[], drawings: BoardDrawing[] = []): string {
  const nodeSig = nodes
    .map(
      (n) =>
        `${n.id}|${n.type}|${n.x}|${n.y}|${n.width ?? ''}|${n.height ?? ''}|${n.zIndex ?? ''}|${n.parentId ?? ''}|${stableData(
          (n.data as unknown as Record<string, unknown>) || {},
        )}`,
    )
    .sort()
    .join('§');
  return `${nodeSig}||${drawingsSignature(drawings)}`;
}

interface RemoteBoard {
  nodes: BoardNode[];
  drawings: BoardDrawing[];
}

interface DrawSnapshot {
  canvas: BoardDrawing[];
  images: Record<string, BoardDrawing[]>;
}

const DRAW_TOOL_KEYS: Record<string, BoardDrawTool> = {
  v: 'select',
  p: 'pen',
  h: 'highlighter',
  l: 'line',
  a: 'arrow',
  r: 'rect',
  o: 'ellipse',
  c: 'callout',
  e: 'eraser',
};

function genId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `node-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/** Ensure parent group nodes appear before their children (React Flow requirement). */
function sortBoardNodes(nodes: BoardNode[]): BoardNode[] {
  const sorted: BoardNode[] = [];
  const remaining = [...nodes];
  while (remaining.length > 0) {
    const idx = remaining.findIndex(
      (n) => !n.parentId || sorted.some((s) => s.id === n.parentId),
    );
    if (idx === -1) {
      sorted.push(...remaining);
      break;
    }
    sorted.push(remaining[idx]);
    remaining.splice(idx, 1);
  }
  return sorted;
}

interface BoardCanvasProps {
  dealId: string;
  timberProjectId?: string;
  migrateFromLeadId?: string;
  embedded?: boolean;
  readOnly?: boolean;
  commentAuthor?: BoardCommentAuthor;
  focusNodeId?: string;
}

function withCommentAnchor(Component: React.ComponentType<NodeProps>) {
  const Wrapped: React.FC<NodeProps> = (props) => (
    <div className="board-node-with-comments">
      <Component {...props} />
      <BoardCommentAnchor nodeId={props.id} />
    </div>
  );
  return Wrapped;
}

const BoardCanvas: React.FC<BoardCanvasProps> = ({
  dealId,
  timberProjectId,
  migrateFromLeadId,
  embedded,
  readOnly = false,
  commentAuthor: commentAuthorProp,
  focusNodeId,
}) => {
  const { user } = useAuth();

  const commentAuthor = useMemo((): BoardCommentAuthor => {
    if (commentAuthorProp) return commentAuthorProp;
    return {
      uid: user?.id || 'unknown',
      displayName: user?.name || user?.email || 'Team Member',
      canWrite: !readOnly,
      kind: 'staff',
    };
  }, [commentAuthorProp, user, readOnly]);

  const nodeTypes = useMemo(
    () => ({
      image: withCommentAnchor(ImageNode),
      link: withCommentAnchor(LinkNode),
      note: withCommentAnchor(NoteNode),
      text: withCommentAnchor(TextNode),
      group: withCommentAnchor(GroupNode),
      timeline: withCommentAnchor(TimelineNode),
      floorplan: withCommentAnchor(FloorPlanNode),
    }),
    [],
  );

  return (
    <BoardCommentsProvider dealId={dealId} author={commentAuthor}>
      <BoardCanvasInner
        dealId={dealId}
        timberProjectId={timberProjectId}
        migrateFromLeadId={migrateFromLeadId}
        embedded={embedded}
        readOnly={readOnly}
        nodeTypes={nodeTypes}
        focusNodeId={focusNodeId}
      />
    </BoardCommentsProvider>
  );
};

const BoardCanvasInner: React.FC<{
  dealId: string;
  timberProjectId?: string;
  migrateFromLeadId?: string;
  embedded?: boolean;
  readOnly?: boolean;
  nodeTypes: Record<string, React.ComponentType<NodeProps>>;
  focusNodeId?: string;
}> = ({ dealId, timberProjectId, migrateFromLeadId, embedded, readOnly = false, nodeTypes, focusNodeId }) => {
  const { user } = useAuth();
  const rf = useReactFlow();
  const [nodes, setNodes, baseOnNodesChange] = useNodesState([]);
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [timelineModal, setTimelineModal] = useState<{
    open: boolean;
    editId?: string;
    initial?: BoardTimelineData;
  }>({ open: false });
  const [floorPlanModal, setFloorPlanModal] = useState<{
    open: boolean;
    editId?: string;
    initial?: BoardFloorPlanData;
  }>({ open: false });
  const [liveFloorPlans, setLiveFloorPlans] = useState<Record<string, BoardFloorPlanData>>({});
  const [liveFloorRevs, setLiveFloorRevs] = useState<Record<string, number>>({});
  const liveFloorPlansRef = useRef<Record<string, BoardFloorPlanData>>({});
  const skipLiveOverlayRef = useRef<Record<string, true> | undefined>(undefined);
  const [snapshotPickerOpen, setSnapshotPickerOpen] = useState(false);
  const [snapshotImporting, setSnapshotImporting] = useState(false);
  const [snapshotImportProgress, setSnapshotImportProgress] = useState<{ current: number; total: number } | null>(null);
  const [htmlCaptureDoc, setHtmlCaptureDoc] = useState<BoardSnapshotModelChoice | null>(null);
  const htmlCaptureCountRef = useRef(0);
  const [canvasDrawings, setCanvasDrawings] = useState<BoardDrawing[]>([]);
  const [drawTool, setDrawTool] = useState<BoardDrawTool>('select');
  const [drawColor, setDrawColor] = useState('#ef4444');
  const [drawWidth, setDrawWidth] = useState<number>(DRAW_WIDTHS[1]);
  const [spaceHeld, setSpaceHeld] = useState(false);
  const [selectedDrawing, setSelectedDrawing] = useState<DrawingSelection | null>(null);
  const drawLayerRef = useRef<BoardDrawLayerHandle | null>(null);
  const [snapEnabled, setSnapEnabled] = useState(loadSnapEnabled);
  const [snapGuides, setSnapGuides] = useState<SnapGuide[]>([]);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const nodesRef = useRef<Node[]>([]);
  const canvasDrawingsRef = useRef<BoardDrawing[]>([]);
  const interactingRef = useRef(false);
  const pendingRemoteRef = useRef<RemoteBoard | null>(null);
  const lastAppliedSigRef = useRef<string>('');
  const hasLoadedRef = useRef(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const undoStackRef = useRef<DrawSnapshot[]>([]);
  const redoStackRef = useRef<DrawSnapshot[]>([]);
  const groupUnitDragRef = useRef<GroupUnitDrag | null>(null);

  useEffect(() => {
    nodesRef.current = nodes;
  }, [nodes]);

  useEffect(() => {
    canvasDrawingsRef.current = canvasDrawings;
  }, [canvasDrawings]);

  const drawing = drawTool !== 'select';

  // --- Persistence -------------------------------------------------------

  const flushSave = useCallback(async () => {
    if (readOnly) return;
    const boardNodes = nodesRef.current.map(toBoardNode);
    const drawings = canvasDrawingsRef.current;
    const sig = boardSignature(boardNodes, drawings);
    // Pre-record our own signature so the resulting snapshot echo is ignored.
    lastAppliedSigRef.current = sig;
    setSaveState('saving');
    try {
      await saveBoard(dealId, boardNodes, rf.getViewport(), user?.email || user?.id, drawings);
      setSaveState('saved');
    } catch (err) {
      console.error('[InspirationBoard] save failed:', err);
      setSaveState('idle');
    }
  }, [dealId, rf, user, readOnly]);

  const scheduleSave = useCallback(() => {
    if (readOnly || !hasLoadedRef.current) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveTimerRef.current = null;
      void flushSave();
    }, SAVE_DEBOUNCE_MS);
  }, [flushSave, readOnly]);

  const applyRemote = useCallback(
    (incoming: RemoteBoard) => {
      const ordered = sortBoardNodes(incoming.nodes);
      lastAppliedSigRef.current = boardSignature(ordered, incoming.drawings);
      setNodes(overlayLiveFloorPlans(
        ordered.map(toRFNode),
        liveFloorPlansRef.current,
        skipLiveOverlayRef.current,
      ));
      setCanvasDrawings(incoming.drawings);
    },
    [setNodes],
  );

  skipLiveOverlayRef.current =
    floorPlanModal.open && floorPlanModal.editId ? { [floorPlanModal.editId]: true } : undefined;

  useEffect(() => {
    liveFloorPlansRef.current = liveFloorPlans;
    setNodes((nds) => overlayLiveFloorPlans(nds, liveFloorPlans, skipLiveOverlayRef.current));
  }, [liveFloorPlans, setNodes, floorPlanModal.open, floorPlanModal.editId]);

  useEffect(
    () => subscribeFloorPlans(dealId, (plans, revs) => {
      setLiveFloorPlans(plans);
      setLiveFloorRevs(revs);
    }),
    [dealId],
  );

  // Real-time subscription + initial load (restores viewport once).
  useEffect(() => {
    setLoading(true);
    hasLoadedRef.current = false;
    const unsub = subscribeToBoard(
      dealId,
      (board) => {
        if (!hasLoadedRef.current) {
          const incoming = sortBoardNodes(board?.nodes || []);
          const drawings = board?.drawings || [];
          lastAppliedSigRef.current = boardSignature(incoming, drawings);
          setNodes(overlayLiveFloorPlans(incoming.map(toRFNode), liveFloorPlansRef.current, skipLiveOverlayRef.current));
          setCanvasDrawings(drawings);
          hasLoadedRef.current = true;
          setLoading(false);
          if (board?.viewport) {
            const vp = board.viewport;
            setTimeout(() => rf.setViewport(vp), 0);
          }
          return;
        }
        const incoming: RemoteBoard = {
          nodes: board?.nodes || [],
          drawings: board?.drawings || [],
        };
        const sig = boardSignature(incoming.nodes, incoming.drawings);
        if (sig === lastAppliedSigRef.current) return; // echo of our own write
        if (interactingRef.current) {
          pendingRemoteRef.current = incoming; // apply when interaction ends
          return;
        }
        applyRemote(incoming);
      },
      () => setLoading(false),
      { migrateFromLeadId },
    );
    return () => {
      unsub();
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [dealId, migrateFromLeadId, rf, setNodes, applyRemote]);

  const { closeComments, openComments, author } = useBoardComments();
  const authorInitials = useMemo(
    () => initialsFromName(author.displayName || user?.name || user?.email || ''),
    [author.displayName, user?.name, user?.email],
  );
  const focusedNodeRef = useRef<string | null>(null);

  useEffect(() => {
    focusedNodeRef.current = null;
  }, [dealId, focusNodeId]);

  useEffect(() => {
    if (loading || !focusNodeId) return;
    if (focusedNodeRef.current === focusNodeId) return;
    if (!nodes.some((n) => n.id === focusNodeId)) return;
    focusedNodeRef.current = focusNodeId;
    const timer = window.setTimeout(() => {
      try {
        rf.fitView({ nodes: [{ id: focusNodeId }], padding: 0.42, duration: 400 });
      } catch {
        /* node may not be measured yet */
      }
      setNodes((nds) => nds.map((n) => ({ ...n, selected: n.id === focusNodeId })));
      openComments(focusNodeId, { highlight: true });
    }, 80);
    return () => window.clearTimeout(timer);
  }, [loading, focusNodeId, nodes, rf, setNodes, openComments]);

  // --- Node change handling ---------------------------------------------

  const handleNodesChange = useCallback(
    (changes: NodeChange[]) => {
      if (readOnly) return;
      const hold = groupUnitDragRef.current;
      const rewritten = hold ? rewriteGroupUnitDragChanges(changes, hold) : { changes, dragged: false };
      if (hold) hold.dragged = rewritten.dragged;
      baseOnNodesChange(rewritten.changes);
      let shouldSave = false;
      for (const c of rewritten.changes) {
        if (c.type === 'position') {
          if (c.dragging) interactingRef.current = true;
          else {
            interactingRef.current = false;
            shouldSave = true;
          }
        } else if (c.type === 'dimensions') {
          if (c.resizing === true) interactingRef.current = true;
          else if (c.resizing === false) {
            interactingRef.current = false;
            shouldSave = true;
          }
        } else if (c.type === 'remove') {
          shouldSave = true;
        }
      }
      if (shouldSave) scheduleSave();
      if (!interactingRef.current && pendingRemoteRef.current) {
        const pending = pendingRemoteRef.current;
        pendingRemoteRef.current = null;
        applyRemote(pending);
      }
    },
    [baseOnNodesChange, scheduleSave, applyRemote, readOnly],
  );

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el || readOnly) return;
    const onDown = (e: PointerEvent) => {
      if (e.button !== 0 || e.shiftKey) return;
      const nodeEl = (e.target as HTMLElement | null)?.closest('.react-flow__node');
      const nodeId = nodeEl?.getAttribute('data-id');
      if (!nodeId) {
        groupUnitDragRef.current = null;
        return;
      }
      const all = nodesRef.current;
      const node = all.find((n) => n.id === nodeId);
      if (!node?.parentId) {
        groupUnitDragRef.current = null;
        return;
      }
      const parent = all.find((n) => n.id === node.parentId);
      if (!parent || parent.type !== 'group' || !parent.selected) {
        groupUnitDragRef.current = null;
        return;
      }
      groupUnitDragRef.current = {
        groupId: parent.id,
        childId: node.id,
        childStart: { ...node.position },
        parentStart: { ...parent.position },
        dragged: false,
      };
    };
    const onUp = () => {
      const hold = groupUnitDragRef.current;
      if (!hold) return;
      groupUnitDragRef.current = null;
      if (hold.dragged) return;
      setNodes((nds) => nds.map((n) => ({ ...n, selected: n.id === hold.childId })));
    };
    el.addEventListener('pointerdown', onDown, true);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      el.removeEventListener('pointerdown', onDown, true);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [readOnly, setNodes]);

  const snapMovingSelection = useCallback(
    (evt: React.MouseEvent, node?: Node) => {
      if (readOnly || drawing || !snapEnabled || evt.altKey) {
        setSnapGuides([]);
        return;
      }
      const all = nodesRef.current;
      const selected = all.filter((n) => n.selected);
      const movingList =
        node && selected.some((n) => n.id === node.id)
          ? selected
          : node
            ? [all.find((n) => n.id === node.id) || node]
            : selected;
      if (movingList.length === 0) return;
      const movingIds = new Set(movingList.map((n) => n.id));
      const independent = movingList.filter((n) => independentlyMoved(n, movingIds));
      const moving = unionSnapRects(independent.map((n) => nodeToSnapRect(n, all)));
      if (!moving) return;
      const others = all.filter((n) => !movingIds.has(n.id)).map((n) => nodeToSnapRect(n, all));
      const zoom = rf.getZoom() || 1;
      const { dx, dy, guides } = snapMovingRect(moving, others, SNAP_SCREEN_PX / zoom);
      setSnapGuides(guides);
      if (!dx && !dy) return;
      setNodes((nds) =>
        nds.map((n) => {
          if (!movingIds.has(n.id) || !independentlyMoved(n, movingIds)) return n;
          return { ...n, position: { x: n.position.x + dx, y: n.position.y + dy } };
        }),
      );
    },
    [drawing, readOnly, rf, setNodes, snapEnabled],
  );

  const handleNodeDragStop = useCallback(() => {
    setSnapGuides([]);
  }, []);

  const toggleSnap = useCallback(() => {
    setSnapEnabled((prev) => {
      const next = !prev;
      saveSnapEnabled(next);
      if (!next) setSnapGuides([]);
      return next;
    });
  }, []);

  // Clean up storage objects for deleted image nodes.
  const handleNodesDelete = useCallback((deleted: Node[]) => {
    deleted.forEach((n) => {
      if (n.type === 'image') {
        const sp = (n.data as BoardImageData)?.storagePath;
        if (sp) void deleteBoardImage(sp);
      }
      void deleteBoardCommentsForNode(dealId, n.id);
    });
  }, [dealId]);

  // --- Node creation helpers --------------------------------------------

  const layerZs = useCallback((): number[] => {
    return [
      ...nodesRef.current.map((n) => (typeof n.zIndex === 'number' ? n.zIndex : 0)),
      ...canvasDrawingsRef.current.map((d) => (typeof d.zIndex === 'number' ? d.zIndex : 0)),
    ];
  }, []);

  const nextZIndex = useCallback((): number => {
    const zs = layerZs();
    return (zs.length ? Math.max(...zs) : 0) + 1;
  }, [layerZs]);

  const bottomZIndex = useCallback((): number => {
    const zs = layerZs();
    return (zs.length ? Math.min(...zs) : 0) - 1;
  }, [layerZs]);

  /** Default placement: center of the current viewport (with small jitter). */
  const centerPosition = useCallback((): XYPosition => {
    const el = wrapperRef.current;
    const rect = el?.getBoundingClientRect();
    const cx = rect ? rect.left + rect.width / 2 : window.innerWidth / 2;
    const cy = rect ? rect.top + rect.height / 2 : window.innerHeight / 2;
    const jitter = (Math.random() - 0.5) * 40;
    return rf.screenToFlowPosition({ x: cx + jitter, y: cy + jitter });
  }, [rf]);

  const addNode = useCallback(
    (type: BoardNodeType, position: XYPosition, data: Record<string, unknown>, size?: { width: number; height: number }) => {
      if (readOnly) return '';
      const id = genId();
      const dims = size || DEFAULT_NODE_SIZE[type];
      const newNode: Node = {
        id,
        type,
        position,
        data,
        style: { width: dims.width, height: dims.height },
        width: dims.width,
        height: dims.height,
        zIndex: nextZIndex(),
        selected: true,
      };
      setNodes((nds) => [...nds.map((n) => ({ ...n, selected: false })), newNode]);
      scheduleSave();
      return id;
    },
    [nextZIndex, setNodes, scheduleSave, readOnly],
  );

  const setNodeSize = useCallback(
    (id: string, width: number, height: number) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, style: { ...(n.style || {}), width, height }, width, height } : n,
        ),
      );
    },
    [setNodes],
  );

  // --- Uploads + links ---------------------------------------------------

  const addImageFromFile = useCallback(
    async (file: File, options?: { alt?: string; caption?: string; at?: XYPosition }) => {
      if (readOnly) return;
      const alt = options?.alt || file.name;
      const pos = options?.at || centerPosition();
      const id = addNode('image', pos, { url: '', alt, caption: options?.caption });
      try {
        const uploaded = await uploadBoardImage(dealId, file);
        const patch: BoardImageData = {
          url: uploaded.url,
          storagePath: uploaded.storagePath,
          naturalWidth: uploaded.naturalWidth,
          naturalHeight: uploaded.naturalHeight,
          alt,
          caption: options?.caption,
        };
        setNodes((nds) => nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, ...patch } } : n)));
        if (uploaded.naturalWidth && uploaded.naturalHeight) {
          const ratio = uploaded.naturalWidth / uploaded.naturalHeight;
          let w = IMAGE_MAX_INITIAL_EDGE;
          let h = IMAGE_MAX_INITIAL_EDGE;
          if (ratio >= 1) h = Math.round(IMAGE_MAX_INITIAL_EDGE / ratio);
          else w = Math.round(IMAGE_MAX_INITIAL_EDGE * ratio);
          setNodeSize(id, w, h);
        }
        scheduleSave();
      } catch (err) {
        console.error('[InspirationBoard] image upload failed:', err);
        setNodes((nds) => nds.filter((n) => n.id !== id));
        window.alert(`Failed to upload "${file.name}".`);
      }
    },
    [dealId, centerPosition, addNode, setNodes, setNodeSize, scheduleSave, readOnly],
  );

  const uploadFiles = useCallback(
    async (files: File[], at?: XYPosition) => {
      if (readOnly) return;
      const images = files.filter((f) => f.type.startsWith('image/') || /\.(heic|heif)$/i.test(f.name));
      if (images.length === 0) return;
      const base = at || centerPosition();
      for (let i = 0; i < images.length; i += 1) {
        const file = images[i];
        await addImageFromFile(file, {
          alt: file.name,
          at: { x: base.x + i * 28, y: base.y + i * 28 },
        });
      }
    },
    [addImageFromFile, centerPosition, readOnly],
  );

  const handlePickPdfPages = useCallback(
    async (choices: BoardSnapshotPdfChoice[]) => {
      if (readOnly || choices.length === 0) return;
      setSnapshotImporting(true);
      setSnapshotImportProgress({ current: 0, total: choices.length });
      const base = centerPosition();
      try {
        for (let i = 0; i < choices.length; i += 1) {
          const choice = choices[i];
          setSnapshotImportProgress({ current: i + 1, total: choices.length });
          const safe = choice.label.replace(/[^\w.-]+/g, '_').slice(0, 80);
          const file = await renderPdfPageToFile(choice.url, choice.pageNumber, `${safe}.jpg`);
          await addImageFromFile(file, {
            alt: choice.label,
            caption: choice.label,
            at: { x: base.x + i * 28, y: base.y + i * 28 },
          });
        }
        setSnapshotPickerOpen(false);
      } catch (err) {
        console.error('[InspirationBoard] PDF snapshot failed:', err);
        window.alert('Could not import one or more PDF pages.');
      } finally {
        setSnapshotImporting(false);
        setSnapshotImportProgress(null);
      }
    },
    [addImageFromFile, centerPosition, readOnly],
  );

  const handlePickModel = useCallback((choice: BoardSnapshotModelChoice) => {
    htmlCaptureCountRef.current = 0;
    setSnapshotPickerOpen(false);
    setHtmlCaptureDoc(choice);
  }, []);

  const handleHtmlCapture = useCallback(
    async (file: File) => {
      const i = htmlCaptureCountRef.current;
      htmlCaptureCountRef.current += 1;
      const base = centerPosition();
      const viewLabel = i === 0 ? 'snapshot' : `snapshot ${i + 1}`;
      const versionBit = htmlCaptureDoc?.versionLabel ? ` · ${htmlCaptureDoc.versionLabel}` : '';
      const label = htmlCaptureDoc ? `${htmlCaptureDoc.title}${versionBit} · ${viewLabel}` : file.name;
      await addImageFromFile(file, {
        alt: label,
        caption: label,
        at: { x: base.x + i * 28, y: base.y + i * 28 },
      });
    },
    [addImageFromFile, centerPosition, htmlCaptureDoc],
  );

  const addLink = useCallback(
    async (rawUrl: string, at?: XYPosition) => {
      if (readOnly) return;
      const url = normalizeUrl(rawUrl);
      if (!url) return;
      const pos = at || centerPosition();
      const id = addNode('link', pos, {
        url,
        domain: domainFromUrl(url),
        loading: true,
      });
      try {
        const preview = await fetchLinkPreview(url);
        const patch: BoardLinkData = {
          url,
          title: preview.title,
          description: preview.description,
          thumbnailUrl: preview.image,
          favicon: preview.favicon,
          domain: preview.domain,
          loading: false,
        };
        setNodes((nds) =>
          nds.map((n) => {
            if (n.id !== id) return n;
            const prev = n.data as BoardLinkData;
            return {
              ...n,
              data: { ...n.data, ...patch, displayTitle: prev.displayTitle },
            };
          }),
        );
        scheduleSave();
      } catch (err) {
        console.error('[InspirationBoard] link preview failed:', err);
        setNodes((nds) =>
          nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, loading: false } } : n)),
        );
        scheduleSave();
      }
    },
    [centerPosition, addNode, setNodes, scheduleSave, readOnly],
  );

  const addNote = useCallback(() => {
    if (readOnly) return;
    setDrawTool('select');
    setSelectedDrawing(null);
    addNode('note', centerPosition(), { text: '', color: DEFAULT_NOTE_COLOR });
  }, [addNode, centerPosition, readOnly]);

  const addText = useCallback(() => {
    if (readOnly) return;
    setDrawTool('select');
    setSelectedDrawing(null);
    addNode('text', centerPosition(), { text: '', textStyle: DEFAULT_BOARD_TEXT_STYLE });
  }, [addNode, centerPosition, readOnly]);

  const addTimeline = useCallback(
    (data: BoardTimelineData) => {
      if (readOnly) return;
      addNode('timeline', centerPosition(), { ...data });
    },
    [addNode, centerPosition, readOnly],
  );

  const startFloorPlan = useCallback(() => {
    if (readOnly) return;
    const data = createDefaultFloorPlan();
    const id = addNode('floorplan', centerPosition(), { ...data });
    if (!id) return;
    setDrawTool('select');
    setSelectedDrawing(null);
    setFloorPlanModal({ open: true, editId: id, initial: data });
  }, [addNode, centerPosition, readOnly]);

  const canGroup = useCallback((): boolean => {
    const selected = nodesRef.current.filter(
      (n) => n.selected && n.type !== 'group' && !n.parentId,
    );
    return selected.length >= 2;
  }, []);

  const canUngroup = useCallback((): boolean => {
    return nodesRef.current.some(
      (n) => n.selected && (n.type === 'group' || !!n.parentId),
    );
  }, []);

  const groupSelection = useCallback(() => {
    if (readOnly) return;
    const all = nodesRef.current;
    const selected = all.filter((n) => n.selected && n.type !== 'group' && !n.parentId);
    if (selected.length < 2) return;

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const n of selected) {
      const abs = getAbsolutePosition(n, all);
      const { width, height } = nodeDimensions(n);
      minX = Math.min(minX, abs.x);
      minY = Math.min(minY, abs.y);
      maxX = Math.max(maxX, abs.x + width);
      maxY = Math.max(maxY, abs.y + height);
    }

    const groupId = genId();
    const groupX = minX - GROUP_PADDING;
    const groupY = minY - GROUP_PADDING - GROUP_LABEL_HEIGHT;
    const groupW = maxX - minX + GROUP_PADDING * 2;
    const groupH = maxY - minY + GROUP_PADDING * 2 + GROUP_LABEL_HEIGHT;
    const minZ = Math.min(...selected.map((n) => (typeof n.zIndex === 'number' ? n.zIndex : 0)));

    const groupNode: Node = {
      id: groupId,
      type: 'group',
      position: { x: groupX, y: groupY },
      data: { label: 'Group' },
      style: { width: groupW, height: groupH },
      width: groupW,
      height: groupH,
      zIndex: minZ - 1,
      selectable: true,
      draggable: true,
      selected: true,
    };

    const selectedIds = new Set(selected.map((n) => n.id));
    const updatedChildren = selected.map((n) => {
      const abs = getAbsolutePosition(n, all);
      return {
        ...n,
        parentId: groupId,
        extent: 'parent' as const,
        position: { x: abs.x - groupX, y: abs.y - groupY },
        selected: false,
      };
    });

    setNodes(() => {
      const combined: Node[] = [
        ...nodesRef.current
          .filter((n) => !selectedIds.has(n.id))
          .map((n) => ({ ...n, selected: false })),
        groupNode,
        ...updatedChildren,
      ];
      return sortBoardNodes(combined.map(toBoardNode))
        .map(toRFNode)
        .map((n) => ({ ...n, selected: n.id === groupId, draggable: true }));
    });
    scheduleSave();
  }, [readOnly, setNodes, scheduleSave]);

  const ungroupSelection = useCallback(() => {
    if (readOnly) return;
    let next = [...nodesRef.current];
    const groupIds = new Set<string>();

    next.filter((n) => n.selected && n.type === 'group').forEach((g) => groupIds.add(g.id));
    next.filter((n) => n.selected && n.parentId).forEach((n) => {
      if (n.parentId) groupIds.add(n.parentId);
    });

    if (groupIds.size === 0) return;

    for (const groupId of Array.from(groupIds)) {
      const group = next.find((n) => n.id === groupId);
      if (!group || group.type !== 'group') continue;
      const gx = group.position.x;
      const gy = group.position.y;
      next = next
        .map((n) => {
          if (n.parentId === groupId) {
            const { parentId: _p, extent: _e, ...rest } = n;
            return {
              ...rest,
              position: { x: n.position.x + gx, y: n.position.y + gy },
            };
          }
          return n;
        })
        .filter((n) => n.id !== groupId);
    }

    setNodes(() => sortBoardNodes(next.map(toBoardNode)).map(toRFNode));
    scheduleSave();
  }, [readOnly, setNodes, scheduleSave]);

  const takeDrawSnapshot = useCallback((): DrawSnapshot => {
    const images: Record<string, BoardDrawing[]> = {};
    nodesRef.current.forEach((n) => {
      if (n.type === 'image') {
        images[n.id] = [...(((n.data as BoardImageData).drawings) || [])];
      }
    });
    return { canvas: [...canvasDrawingsRef.current], images };
  }, []);

  const applyDrawSnapshot = useCallback(
    (snap: DrawSnapshot) => {
      setCanvasDrawings(snap.canvas);
      setNodes((nds) =>
        nds.map((n) => {
          if (n.type !== 'image') return n;
          return { ...n, data: { ...n.data, drawings: snap.images[n.id] || [] } };
        }),
      );
      scheduleSave();
    },
    [setNodes, scheduleSave],
  );

  const pushDrawUndo = useCallback(() => {
    undoStackRef.current.push(takeDrawSnapshot());
    if (undoStackRef.current.length > 50) undoStackRef.current.shift();
    redoStackRef.current = [];
  }, [takeDrawSnapshot]);

  const undoDraw = useCallback(() => {
    const prev = undoStackRef.current.pop();
    if (!prev) return;
    redoStackRef.current.push(takeDrawSnapshot());
    applyDrawSnapshot(prev);
  }, [takeDrawSnapshot, applyDrawSnapshot]);

  const redoDraw = useCallback(() => {
    const next = redoStackRef.current.pop();
    if (!next) return;
    undoStackRef.current.push(takeDrawSnapshot());
    applyDrawSnapshot(next);
  }, [takeDrawSnapshot, applyDrawSnapshot]);

  const handleAddCanvasDrawing = useCallback(
    (drawing: BoardDrawing) => {
      interactingRef.current = false;
      const zIndex =
        drawing.zIndex ?? (isFrameDrawing(drawing) ? bottomZIndex() : nextZIndex());
      const stamped = { ...drawing, zIndex };
      setCanvasDrawings((ds) => [...ds, stamped]);
      scheduleSave();
    },
    [scheduleSave, nextZIndex, bottomZIndex],
  );

  const handleAddImageDrawing = useCallback(
    (nodeId: string, drawing: BoardDrawing) => {
      if (isFrameDrawing(drawing)) {
        handleAddCanvasDrawing(drawing);
        return;
      }
      interactingRef.current = false;
      const stamped = { ...drawing, zIndex: drawing.zIndex ?? nextZIndex() };
      setNodes((nds) =>
        nds.map((n) => {
          if (n.id !== nodeId) return n;
          const existing = ((n.data as BoardImageData).drawings) || [];
          return { ...n, data: { ...n.data, drawings: [...existing, stamped] } };
        }),
      );
      scheduleSave();
    },
    [setNodes, scheduleSave, nextZIndex, handleAddCanvasDrawing],
  );

  const handleEraseCanvas = useCallback(
    (ids: string[]) => {
      if (ids.length === 0) return;
      setCanvasDrawings((ds) => ds.filter((d) => !ids.includes(d.id)));
      scheduleSave();
    },
    [scheduleSave],
  );

  const handleEraseImage = useCallback(
    (nodeId: string, ids: string[]) => {
      if (ids.length === 0) return;
      setNodes((nds) =>
        nds.map((n) => {
          if (n.id !== nodeId) return n;
          const existing = ((n.data as BoardImageData).drawings) || [];
          return { ...n, data: { ...n.data, drawings: existing.filter((d) => !ids.includes(d.id)) } };
        }),
      );
      scheduleSave();
    },
    [setNodes, scheduleSave],
  );

  const handleUpdateDrawing = useCallback(
    (target: 'canvas' | string, drawing: BoardDrawing) => {
      if (target === 'canvas') {
        setCanvasDrawings((ds) => ds.map((d) => (d.id === drawing.id ? drawing : d)));
      } else {
        setNodes((nds) =>
          nds.map((n) => {
            if (n.id !== target) return n;
            const existing = ((n.data as BoardImageData).drawings) || [];
            return {
              ...n,
              data: { ...n.data, drawings: existing.map((d) => (d.id === drawing.id ? drawing : d)) },
            };
          }),
        );
      }
      scheduleSave();
    },
    [setNodes, scheduleSave],
  );

  const handleUpdateCallout = useCallback(
    (target: 'canvas' | string, id: string, text: string) => {
      if (target === 'canvas') {
        setCanvasDrawings((ds) => ds.map((d) => (d.id === id ? { ...d, text: text || undefined } : d)));
      } else {
        setNodes((nds) =>
          nds.map((n) => {
            if (n.id !== target) return n;
            const existing = ((n.data as BoardImageData).drawings) || [];
            return {
              ...n,
              data: {
                ...n.data,
                drawings: existing.map((d) => (d.id === id ? { ...d, text: text || undefined } : d)),
              },
            };
          }),
        );
      }
      scheduleSave();
    },
    [setNodes, scheduleSave],
  );

  const handleDrawToolChange = useCallback((next: BoardDrawTool) => {
    setDrawTool(next);
    if (next === 'highlighter' && !(HIGHLIGHTER_WIDTHS as readonly number[]).includes(drawWidth)) {
      setDrawWidth(HIGHLIGHTER_WIDTHS[1]);
    }
    if (next !== 'highlighter' && next !== 'select' && next !== 'eraser'
      && !(DRAW_WIDTHS as readonly number[]).includes(drawWidth)) {
      setDrawWidth(DRAW_WIDTHS[1]);
    }
  }, [drawWidth]);

  const deleteSelectedDrawing = useCallback(() => {
    if (!selectedDrawing) return;
    if (selectedDrawing.target === 'canvas') handleEraseCanvas([selectedDrawing.id]);
    else handleEraseImage(selectedDrawing.target, [selectedDrawing.id]);
    setSelectedDrawing(null);
  }, [selectedDrawing, handleEraseCanvas, handleEraseImage]);

  const deleteSelectedElements = useCallback(() => {
    if (selectedDrawing) {
      deleteSelectedDrawing();
      return;
    }
    let selected = nodesRef.current.filter((n) => n.selected);
    if (selected.length === 0) return;
    const groupIds = new Set(selected.filter((n) => n.type === 'group').map((n) => n.id));
    if (groupIds.size > 0) {
      const children = nodesRef.current.filter((n) => n.parentId && groupIds.has(n.parentId));
      selected = [...selected, ...children];
    }
    handleNodesDelete(selected);
    const ids = new Set(selected.map((n) => n.id));
    setNodes((nds) => nds.filter((n) => !ids.has(n.id)));
    scheduleSave();
  }, [selectedDrawing, deleteSelectedDrawing, handleNodesDelete, setNodes, scheduleSave]);

  const styleSelectedDrawing = useCallback(
    (patch: { color?: string; width?: number; zIndex?: number }) => {
      if (!selectedDrawing) return;
      const apply = (d: BoardDrawing): BoardDrawing => {
        let width = patch.width ?? d.width;
        if (patch.width != null && selectedDrawing.target !== 'canvas') {
          const node = nodesRef.current.find((n) => n.id === selectedDrawing.target);
          const media = node ? imageMediaRect(node, nodesRef.current) : null;
          if (media) width = patch.width / media.width;
        }
        return { ...d, ...patch, width };
      };
      if (selectedDrawing.target === 'canvas') {
        setCanvasDrawings((ds) => ds.map((d) => (d.id === selectedDrawing.id ? apply(d) : d)));
      } else {
        const nodeId = selectedDrawing.target;
        setNodes((nds) =>
          nds.map((n) => {
            if (n.id !== nodeId) return n;
            const existing = ((n.data as BoardImageData).drawings) || [];
            return { ...n, data: { ...n.data, drawings: existing.map((d) => (d.id === selectedDrawing.id ? apply(d) : d)) } };
          }),
        );
      }
      scheduleSave();
    },
    [selectedDrawing, setNodes, scheduleSave],
  );

  const selectedDrawingShape = useMemo((): BoardDrawing | null => {
    if (!selectedDrawing) return null;
    if (selectedDrawing.target === 'canvas') {
      return canvasDrawings.find((d) => d.id === selectedDrawing.id) || null;
    }
    const node = nodes.find((n) => n.id === selectedDrawing.target);
    const drawings = ((node?.data as BoardImageData | undefined)?.drawings) || [];
    return drawings.find((d) => d.id === selectedDrawing.id) || null;
  }, [selectedDrawing, canvasDrawings, nodes]);

  const restackSelectedDrawing = useCallback(
    (edge: 'front' | 'back') => {
      if (!selectedDrawing) return;
      if (edge === 'front' && selectedDrawingShape && isFrameDrawing(selectedDrawingShape)) {
        return;
      }
      const zs = layerZs();
      const zIndex =
        edge === 'front'
          ? (zs.length ? Math.max(...zs) : 0) + 1
          : (zs.length ? Math.min(...zs) : 0) - 1;
      styleSelectedDrawing({ zIndex });
    },
    [selectedDrawing, selectedDrawingShape, layerZs, styleSelectedDrawing],
  );

  const behindDrawings = useMemo(() => {
    const maxZ = maxNodeZIndex(nodes);
    return [
      ...canvasDrawings.filter((d) => !drawingIsFront(d, maxZ)),
      ...imageCalloutsInFlow(nodes)
        .filter((c) => !drawingIsFront(c.flow, maxZ))
        .map((c) => c.flow),
    ];
  }, [nodes, canvasDrawings]);

  const editTimeline = useCallback((id: string) => {
    const node = nodesRef.current.find((n) => n.id === id);
    if (!node || node.type !== 'timeline') return;
    setTimelineModal({
      open: true,
      editId: id,
      initial: node.data as BoardTimelineData,
    });
  }, []);

  const editFloorPlan = useCallback((id: string) => {
    const node = nodesRef.current.find((n) => n.id === id);
    if (!node || node.type !== 'floorplan') return;
    setFloorPlanModal({
      open: true,
      editId: id,
      initial: node.data as BoardFloorPlanData,
    });
  }, []);

  // --- Board actions exposed to nodes -----------------------------------

  const actions = useMemo<BoardActions>(
    () => ({
      readOnly,
      drawTool,
      selectedDrawing,
      selectDrawing: setSelectedDrawing,
      startDrawingEdit: (drawing, target, e) => {
        drawLayerRef.current?.beginEdit(drawing, target, e);
      },
      updateNodeData: (id, patch) => {
        if (readOnly) return;
        setNodes((nds) =>
          nds.map((n) => {
            if (n.id !== id) return n;
            if (n.type === 'image' && Object.prototype.hasOwnProperty.call(patch, 'caption')) {
              const { caption, ...rest } = patch;
              const merged = Object.keys(rest).length ? { ...n, data: { ...n.data, ...rest } } : n;
              return withImageCaption(merged, caption as string | undefined);
            }
            return { ...n, data: { ...n.data, ...patch } };
          }),
        );
        scheduleSave();
      },
      deleteNode: (id) => {
        if (readOnly) return;
        const node = nodesRef.current.find((n) => n.id === id);
        if (node?.type === 'image') {
          const sp = (node.data as BoardImageData)?.storagePath;
          if (sp) void deleteBoardImage(sp);
        }
        if (node?.type === 'group') {
          void deleteBoardCommentsForNode(dealId, id);
          const gx = node.position.x;
          const gy = node.position.y;
          setNodes((nds) => {
            const withoutGroup = nds.filter((n) => n.id !== id);
            return withoutGroup.map((n) => {
              if (n.parentId !== id) return n;
              const { parentId: _p, extent: _e, ...rest } = n;
              return {
                ...rest,
                position: { x: n.position.x + gx, y: n.position.y + gy },
              };
            });
          });
          scheduleSave();
          return;
        }
        void deleteBoardCommentsForNode(dealId, id);
        setNodes((nds) => nds.filter((n) => n.id !== id));
        scheduleSave();
      },
      duplicateNode: (id) => {
        if (readOnly) return;
        const node = nodesRef.current.find((n) => n.id === id);
        if (!node || node.type === 'group') return;
        const data = { ...node.data };
        if (node.type === 'image') delete (data as BoardImageData).storagePath;
        const copy: Node = {
          ...node,
          id: genId(),
          parentId: undefined,
          extent: undefined,
          position: { x: node.position.x + 28, y: node.position.y + 28 },
          data,
          selected: true,
          zIndex: nextZIndex(),
        };
        setNodes((nds) => [...nds.map((n) => ({ ...n, selected: false })), copy]);
        scheduleSave();
      },
      bringToFront: (id) => {
        if (readOnly) return;
        const zs = layerZs();
        const top = (zs.length ? Math.max(...zs) : 0) + 1;
        setNodes((nds) => nds.map((n) => (n.id === id ? { ...n, zIndex: top } : n)));
        scheduleSave();
      },
      sendToBack: (id) => {
        if (readOnly) return;
        const zs = layerZs();
        const bottom = (zs.length ? Math.min(...zs) : 0) - 1;
        setNodes((nds) => nds.map((n) => (n.id === id ? { ...n, zIndex: bottom } : n)));
        scheduleSave();
      },
      rotateImage: (id) => {
        if (readOnly) return;
        const node = nodesRef.current.find((n) => n.id === id);
        if (!node || node.type !== 'image') return;
        const data = node.data as BoardImageData;
        const rotation = nextImageRotation(data.rotation);
        const { width, height } = nodeSize(node);
        const captionH = data.caption ? IMAGE_CAPTION_HEIGHT : 0;
        const mediaW = width;
        const mediaH = Math.max(8, height - captionH);
        const nextW = mediaH;
        const nextH = mediaW + captionH;
        setNodes((nds) =>
          nds.map((n) => {
            if (n.id !== id) return n;
            return {
              ...n,
              width: nextW,
              height: nextH,
              style: { ...(n.style || {}), width: nextW, height: nextH },
              data: {
                ...data,
                rotation,
                drawings: rotateImageDrawingsCW(data.drawings),
              },
            };
          }),
        );
        scheduleSave();
      },
      groupSelection,
      ungroupSelection,
      canGroup,
      canUngroup,
      editTimeline,
      editFloorPlan,
    }),
    [
      dealId,
      setNodes,
      scheduleSave,
      nextZIndex,
      layerZs,
      readOnly,
      drawTool,
      selectedDrawing,
      groupSelection,
      ungroupSelection,
      canGroup,
      canUngroup,
      editTimeline,
      editFloorPlan,
    ],
  );

  const toolbarTools = useMemo<BoardToolItem[]>(() => {
    if (readOnly) return [];
    return [
      {
        id: 'floor-plan',
        label: 'Floor plan',
        icon: <House size={16} />,
        onClick: startFloorPlan,
      },
      {
        id: 'timeline-builder',
        label: 'Quick Timeline Builder',
        icon: <GanttChart size={16} />,
        onClick: () => setTimelineModal({ open: true }),
      },
      {
        id: 'group',
        label: 'Group selection',
        icon: <Group size={16} />,
        onClick: groupSelection,
        disabled: !canGroup(),
      },
      {
        id: 'ungroup',
        label: 'Ungroup',
        icon: <Ungroup size={16} />,
        onClick: ungroupSelection,
        disabled: !canUngroup(),
      },
    ];
  }, [readOnly, groupSelection, ungroupSelection, canGroup, canUngroup, nodes, startFloorPlan]);

  // --- Drag & drop / paste ----------------------------------------------

  const onDragOver = useCallback((e: React.DragEvent) => {
    if (readOnly) return;
    if (Array.from(e.dataTransfer.types).includes('Files')) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      setIsDraggingFile(true);
    }
  }, [readOnly]);

  const onDragLeave = useCallback((e: React.DragEvent) => {
    if (readOnly) return;
    if (e.currentTarget === e.target) setIsDraggingFile(false);
  }, [readOnly]);

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      if (readOnly) return;
      e.preventDefault();
      setIsDraggingFile(false);
      const pos = rf.screenToFlowPosition({ x: e.clientX, y: e.clientY });
      const files = Array.from(e.dataTransfer.files || []);
      if (files.length) {
        void uploadFiles(files, pos);
        return;
      }
      const text = e.dataTransfer.getData('text/plain');
      if (text && looksLikeUrl(text)) void addLink(text, pos);
    },
    [rf, uploadFiles, addLink, readOnly],
  );

  // Document-level paste (ignored while editing a note/text field).
  useEffect(() => {
    if (readOnly) return;
    const onPaste = (e: ClipboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.isContentEditable || /^(INPUT|TEXTAREA)$/.test(target.tagName))) return;
      const items = e.clipboardData?.items;
      if (!items) return;
      const files: File[] = [];
      for (let i = 0; i < items.length; i += 1) {
        if (items[i].kind === 'file') {
          const f = items[i].getAsFile();
          if (f) files.push(f);
        }
      }
      if (files.length) {
        e.preventDefault();
        void uploadFiles(files);
        return;
      }
      const text = e.clipboardData?.getData('text/plain') || '';
      if (text && looksLikeUrl(text)) {
        e.preventDefault();
        void addLink(text);
      }
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [uploadFiles, addLink, readOnly]);

  // Keyboard: Cmd/Ctrl+D duplicates; draw tool shortcuts; undo/redo drawings.
  useEffect(() => {
    if (readOnly) return;
    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.isContentEditable || /^(INPUT|TEXTAREA)$/.test(target.tagName))) return;
      if (e.code === 'Space') {
        e.preventDefault();
        if (!e.repeat) setSpaceHeld(true);
        return;
      }
      if ((e.metaKey || e.ctrlKey) && (e.key === 'z' || e.key === 'Z')) {
        e.preventDefault();
        if (e.shiftKey) redoDraw();
        else undoDraw();
        return;
      }
      if ((e.metaKey || e.ctrlKey) && (e.key === 'd' || e.key === 'D')) {
        e.preventDefault();
        nodesRef.current.filter((n) => n.selected).forEach((n) => actions.duplicateNode(n.id));
        return;
      }
      if (e.key === 'Backspace' || e.key === 'Delete') {
        e.preventDefault();
        deleteSelectedElements();
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const mapped = DRAW_TOOL_KEYS[e.key.toLowerCase()];
      if (mapped) {
        e.preventDefault();
        handleDrawToolChange(mapped);
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') setSpaceHeld(false);
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('keyup', onKeyUp);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('keyup', onKeyUp);
    };
  }, [actions, readOnly, undoDraw, redoDraw, handleDrawToolChange, deleteSelectedElements]);

  const minimapColor = useCallback((n: Node): string => {
    switch (n.type) {
      case 'image':
        return '#6366f1';
      case 'link':
        return '#0ea5e9';
      case 'note':
        return '#f59e0b';
      case 'timeline':
        return '#10b981';
      case 'floorplan':
        return '#243528';
      case 'group':
        return '#a78bfa';
      default:
        return '#94a3b8';
    }
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      closeComments();
      setDrawTool('select');
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [closeComments]);

  // --- Live cursor presence ----------------------------------------------

  const publishCursor = author.canWrite && author.uid !== 'unknown';
  const cursorColor = useMemo(() => cursorColorForUid(author.uid), [author.uid]);
  const lastCursorSentRef = useRef(0);

  const handlePresenceMove = useCallback(
    (e: React.PointerEvent) => {
      if (!publishCursor || floorPlanModal.open) return;
      const now = Date.now();
      if (now - lastCursorSentRef.current < 150) return;
      lastCursorSentRef.current = now;
      const pos = rf.screenToFlowPosition({ x: e.clientX, y: e.clientY });
        void publishBoardCursor(dealId, {
        uid: author.uid,
        name: author.displayName,
        color: cursorColor,
        x: pos.x,
        y: pos.y,
        surface: 'board',
      });
    },
    [publishCursor, floorPlanModal.open, rf, dealId, author.uid, author.displayName, cursorColor],
  );

  const handlePresenceLeave = useCallback(() => {
    if (!publishCursor) return;
    void clearBoardCursor(dealId, author.uid);
  }, [publishCursor, dealId, author.uid]);

  useEffect(() => {
    if (!publishCursor) return;
    return () => {
      void clearBoardCursor(dealId, author.uid);
    };
  }, [publishCursor, dealId, author.uid]);

  // Trackpad pinch arrives as ctrl+wheel. React Flow zooms the canvas with it,
  // but over `nowheel` areas (note editors, comment bubbles, inputs) the event
  // bubbles to the browser and zooms the whole page — and that zoom sticks.
  // Block it anywhere inside the board; Safari needs the gesture* events too.
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (e.ctrlKey) e.preventDefault();
    };
    const onGesture = (e: Event) => e.preventDefault();
    el.addEventListener('wheel', onWheel, { passive: false });
    el.addEventListener('gesturestart', onGesture as EventListener);
    el.addEventListener('gesturechange', onGesture as EventListener);
    el.addEventListener('gestureend', onGesture as EventListener);
    return () => {
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('gesturestart', onGesture as EventListener);
      el.removeEventListener('gesturechange', onGesture as EventListener);
      el.removeEventListener('gestureend', onGesture as EventListener);
    };
  }, []);

  return (
    <BoardActionsContext.Provider value={actions}>
      <div
        className={`inspiration-board ${embedded ? 'inspiration-board--embedded' : ''} ${isDraggingFile ? 'is-drag-over' : ''} ${readOnly ? 'inspiration-board--readonly' : ''} ${drawing ? 'inspiration-board--drawing' : ''} ${floorPlanModal.open ? 'inspiration-board--floorplan' : ''}`}
        ref={wrapperRef}
        onDragOver={readOnly ? undefined : onDragOver}
        onDragLeave={readOnly ? undefined : onDragLeave}
        onDrop={readOnly ? undefined : onDrop}
        onPointerMove={handlePresenceMove}
        onPointerLeave={handlePresenceLeave}
      >
        <ReactFlow
          nodes={nodes}
          onNodesChange={handleNodesChange}
          onPaneClick={() => {
            closeComments();
            setSelectedDrawing(null);
          }}
          onNodeClick={() => setSelectedDrawing(null)}
          nodeTypes={nodeTypes}
          nodesConnectable={false}
          nodesDraggable={!readOnly && !drawing}
          selectNodesOnDrag={false}
          onNodeDrag={readOnly || drawing ? undefined : (evt, node) => snapMovingSelection(evt, node)}
          onSelectionDrag={readOnly || drawing ? undefined : (evt) => snapMovingSelection(evt)}
          onNodeDragStop={readOnly || drawing ? undefined : handleNodeDragStop}
          onSelectionDragStop={readOnly || drawing ? undefined : handleNodeDragStop}
          elementsSelectable={!readOnly && !drawing}
          deleteKeyCode={null}
          multiSelectionKeyCode={'Shift'}
          selectionKeyCode={readOnly || drawing ? null : 'Shift'}
          selectionOnDrag={!readOnly && !drawing}
          selectionMode={SelectionMode.Partial}
          panOnDrag={[1, 2]}
          panOnScroll
          preventScrolling
          zoomOnDoubleClick={false}
          minZoom={0.1}
          maxZoom={4}
          proOptions={{ hideAttribution: false }}
        >
          <Background variant={BackgroundVariant.Dots} gap={22} size={1.4} color="#d8dee9" />
          <BoardDrawBehind drawings={behindDrawings} selectedId={selectedDrawing?.id ?? null} />
          <Controls showInteractive={false} />
          <MiniMap nodeColor={minimapColor} maskColor="rgba(244,246,250,0.7)" pannable zoomable />
        </ReactFlow>

        <BoardSnapGuides guides={snapGuides} />

        <BoardPresenceLayer dealId={dealId} selfUid={author.uid} />

        <BoardDrawLayer
          ref={drawLayerRef}
          tool={readOnly ? 'select' : drawTool}
          color={drawColor}
          strokeWidth={drawWidth}
          spaceHeld={spaceHeld}
          canvasDrawings={canvasDrawings}
          nodes={nodes}
          selected={selectedDrawing}
          onSelect={(sel) => {
            setSelectedDrawing(sel);
            if (sel) {
              setNodes((nds) => nds.map((n) => ({ ...n, selected: false })));
              if (drawTool === 'rect' || drawTool === 'ellipse' || drawTool === 'line' || drawTool === 'arrow' || drawTool === 'callout') {
                setDrawTool('select');
              }
            }
          }}
          onAddCanvas={handleAddCanvasDrawing}
          onAddImage={handleAddImageDrawing}
          onEraseCanvas={handleEraseCanvas}
          onEraseImage={handleEraseImage}
          onUpdateDrawing={handleUpdateDrawing}
          onUpdateCallout={handleUpdateCallout}
          authorInitials={authorInitials}
          authorName={author.displayName || user?.name}
          authorId={author.uid !== 'unknown' ? author.uid : user?.id}
          onActiveChange={(active) => {
            if (active) pushDrawUndo();
            interactingRef.current = active;
            if (!active && pendingRemoteRef.current) {
              const pending = pendingRemoteRef.current;
              pendingRemoteRef.current = null;
              applyRemote(pending);
            }
          }}
        />

        {!readOnly && !floorPlanModal.open && (
          <BoardDrawToolbar
            tool={drawTool}
            color={drawColor}
            strokeWidth={drawWidth}
            onToolChange={handleDrawToolChange}
            hasSelection={!!selectedDrawing}
            onBringToFront={
              selectedDrawingShape && isFrameDrawing(selectedDrawingShape)
                ? undefined
                : () => restackSelectedDrawing('front')
            }
            onSendToBack={() => restackSelectedDrawing('back')}
            onColorChange={(c) => {
              setDrawColor(c);
              styleSelectedDrawing({ color: c });
            }}
            onWidthChange={(w) => {
              setDrawWidth(w);
              styleSelectedDrawing({ width: w });
            }}
          />
        )}

        {!readOnly && !floorPlanModal.open && (
          <BoardToolbar
            onAddNote={addNote}
            onAddText={addText}
            onUploadFiles={(files) => void uploadFiles(files)}
            onAddLink={(url) => void addLink(url)}
            onOpenSnapshots={timberProjectId ? () => setSnapshotPickerOpen(true) : undefined}
            onAddFloorPlan={startFloorPlan}
            tools={toolbarTools}
            snapEnabled={snapEnabled}
            onToggleSnap={toggleSnap}
          />
        )}

        {!readOnly && timberProjectId && (
          <BoardSnapshotPicker
            open={snapshotPickerOpen}
            projectId={timberProjectId}
            importing={snapshotImporting}
            importProgress={snapshotImportProgress}
            onClose={() => {
              if (!snapshotImporting) setSnapshotPickerOpen(false);
            }}
            onPickPdfPages={choices => void handlePickPdfPages(choices)}
            onPickModel={handlePickModel}
          />
        )}

        {htmlCaptureDoc && (
          <HtmlViewerModal
            url={htmlCaptureDoc.url}
            fetchUrl={htmlCaptureDoc.fetchUrl}
            title={htmlCaptureDoc.title}
            versionLabel={htmlCaptureDoc.versionLabel}
            captureMode
            onCapture={file => void handleHtmlCapture(file)}
            onClose={() => setHtmlCaptureDoc(null)}
          />
        )}

        {!readOnly && (
          <TimelineBuilderModal
            open={timelineModal.open}
            initial={timelineModal.initial}
            onClose={() => setTimelineModal({ open: false })}
            onSubmit={(data) => {
              if (timelineModal.editId) {
                actions.updateNodeData(timelineModal.editId, { ...data });
              } else {
                addTimeline(data);
              }
              setTimelineModal({ open: false });
            }}
          />
        )}

        {!readOnly && (
          <FloorPlanEditor
            open={floorPlanModal.open}
            dealId={dealId}
            nodeId={floorPlanModal.editId}
            timberProjectId={timberProjectId}
            initial={floorPlanModal.initial}
            live={floorPlanModal.editId ? liveFloorPlans[floorPlanModal.editId] || null : null}
            liveRev={floorPlanModal.editId ? liveFloorRevs[floorPlanModal.editId] || 0 : 0}
            presence={
              publishCursor && floorPlanModal.editId
                ? {
                    dealId,
                    uid: author.uid,
                    name: author.displayName,
                    color: cursorColor,
                    nodeId: floorPlanModal.editId,
                  }
                : null
            }
            onAutosave={(data) => {
              if (!floorPlanModal.editId) return;
              const node = nodesRef.current.find((entry) => entry.id === floorPlanModal.editId);
              const current = node?.data as BoardFloorPlanData | undefined;
              if (current && liveDropsEntities(current, data) && floorPlanEntityCount(data) === 0) {
                return;
              }
              actions.updateNodeData(floorPlanModal.editId, { ...data });
            }}
            onClose={() => setFloorPlanModal({ open: false })}
            onSubmit={(data) => {
              if (floorPlanModal.editId) {
                const node = nodesRef.current.find((entry) => entry.id === floorPlanModal.editId);
                const current = node?.data as BoardFloorPlanData | undefined;
                if (!(current && liveDropsEntities(current, data) && floorPlanEntityCount(data) === 0)) {
                  actions.updateNodeData(floorPlanModal.editId, { ...data });
                }
              }
              setFloorPlanModal({ open: false });
            }}
          />
        )}

        {loading && (
          <div className="board-loading-overlay">
            <div className="board-spinner" />
          </div>
        )}

        {!readOnly && (
          <div className={`board-save-indicator ${saveState}`}>
            {saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? 'All changes saved' : ''}
          </div>
        )}

        {!readOnly && isDraggingFile && (
          <div className="board-drop-hint">Drop images to add them to the board</div>
        )}
      </div>
    </BoardActionsContext.Provider>
  );
};

interface InspirationBoardProps {
  dealId: string;
  timberProjectId?: string;
  migrateFromLeadId?: string;
  embedded?: boolean;
  readOnly?: boolean;
  commentAuthor?: BoardCommentAuthor;
  focusNodeId?: string;
}

const InspirationBoard: React.FC<InspirationBoardProps> = ({
  dealId,
  timberProjectId,
  migrateFromLeadId,
  embedded,
  readOnly,
  commentAuthor,
  focusNodeId,
}) => (
  <div className="inspiration-board-shell">
    <ReactFlowProvider>
      <BoardCanvas
        dealId={dealId}
        timberProjectId={timberProjectId}
        migrateFromLeadId={migrateFromLeadId}
        embedded={embedded}
        readOnly={readOnly}
        commentAuthor={commentAuthor}
        focusNodeId={focusNodeId}
      />
    </ReactFlowProvider>
  </div>
);

export default InspirationBoard;
